import type { RawParams } from '@uirouter/core';

import { SecurityGroupDetails } from './SecurityGroupDetails';
import { SecurityGroups } from './SecurityGroups';
import { StandaloneSecurityGroupDetails } from './StandaloneSecurityGroupDetails';
import type { Application, ApplicationStateProvider } from '../application';
import { ApplicationModelBuilder, registerApplicationState } from '../application';
import { filterModelConfig } from './filter/SecurityGroupFilterModel';
import { SecurityGroupFilters } from './filter/SecurityGroupFilters';
import { FirewallLabels } from './label';
import type { INestedState, StateConfigProvider } from '../navigation';
import { registerRootState } from '../navigation/rootState.registration';
import type { SecurityGroupReader } from './securityGroupReader.service';

export function getStandaloneFirewallState(securityGroupReader: SecurityGroupReader): INestedState {
  return {
    name: 'firewallDetails',
    url: '/firewallDetails/:provider/:accountId/:region/:vpcId/:name',
    params: {
      vpcId: {
        value: null,
        squash: true,
      },
    },
    views: {
      'main@': {
        component: StandaloneSecurityGroupDetails,
        $type: 'react',
      },
    },
    resolve: {
      resolvedSecurityGroup: [
        '$stateParams',
        ($stateParams: RawParams) => {
          return {
            name: $stateParams.name,
            accountId: $stateParams.accountId,
            provider: $stateParams.provider,
            region: $stateParams.region,
            vpcId: $stateParams.vpcId,
          };
        },
      ],
      app: [
        '$stateParams',
        ($stateParams: RawParams): Promise<Application> => {
          // we need the application to have a firewall index (so rules get attached and linked properly)
          // and its name should just be the name of the firewall (so cloning works as expected)
          return securityGroupReader.loadSecurityGroups().then((securityGroupsIndex) => {
            const application: Application = ApplicationModelBuilder.createStandaloneApplication($stateParams.name);
            application['securityGroupsIndex'] = securityGroupsIndex; // TODO: refactor the securityGroupsIndex out
            return application;
          });
        },
      ],
    },
    data: {
      pageTitleDetails: {
        title: `${FirewallLabels.get('Firewall')} Details`,
        nameParam: 'name',
        accountParam: 'accountId',
        regionParam: 'region',
      },
      history: {
        type: 'securityGroups',
      },
    },
  };
}

registerApplicationState(
  (applicationStateProvider: ApplicationStateProvider, stateConfigProvider: StateConfigProvider) => {
    const firewallDetails: INestedState = {
      name: 'firewallDetails',
      url: '/firewallDetails/:provider/:accountId/:region/:vpcId/:name',
      params: {
        vpcId: {
          value: null,
          squash: true,
        },
      },
      views: {
        'detail@../insight': {
          component: SecurityGroupDetails,
          $type: 'react',
        },
      },
      resolve: {
        accountId: ['$stateParams', ($stateParams: RawParams) => $stateParams.accountId],
        resolvedSecurityGroup: [
          '$stateParams',
          ($stateParams: RawParams) => {
            return {
              name: $stateParams.name,
              accountId: $stateParams.accountId,
              provider: $stateParams.provider,
              region: $stateParams.region,
              vpcId: $stateParams.vpcId,
            };
          },
        ],
      },
      data: {
        pageTitleDetails: {
          title: `${FirewallLabels.get('Firewall')} Details`,
          nameParam: 'name',
          accountParam: 'accountId',
          regionParam: 'region',
        },
        history: {
          type: 'securityGroups',
        },
      },
    };

    const securityGroupSummary: INestedState = {
      url: `/firewalls?${stateConfigProvider.paramsToQuery(filterModelConfig)}`,
      name: 'firewalls',
      views: {
        nav: { component: SecurityGroupFilters, $type: 'react' },
        master: {
          component: SecurityGroups,
          $type: 'react',
        },
      },
      params: stateConfigProvider.buildDynamicParams(filterModelConfig),
      data: {
        pageTitleSection: {
          title: FirewallLabels.get('Firewalls'),
        },
      },
    };

    applicationStateProvider.addInsightState(securityGroupSummary);
    applicationStateProvider.addInsightDetailState(firewallDetails);
    stateConfigProvider.addRewriteRule(
      '/applications/{application}/securityGroups',
      '/applications/{application}/firewalls',
    );
    stateConfigProvider.addRewriteRule(/(.+?)\/securityGroupDetails\/(.*)/, ($match: string[]) => {
      return `${$match[1]}/firewallDetails/${$match[2]}`;
    });
  },
);

registerRootState((stateConfigProvider) =>
  stateConfigProvider.addToRootState(
    getStandaloneFirewallState(stateConfigProvider.runtimeServices.securityGroupReader),
  ),
);
